import { useSnackbar } from 'notistack';

import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import { Avatar, MenuList } from '@mui/material';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';

import { userTypeChoices } from 'src/utils/translate-enum-to-porteguese';

import {
  GetProfileQuery,
  PermissionUserType,
  useUpdateMyConnectedStoreMutation,
} from 'src/graphql/generated';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export default function ConnectedStorePopover({ profile }: { profile: GetProfileQuery }) {
  const popover = usePopover();

  const [executeUpdateConnectedStore, updateConnectedStoreData] =
    useUpdateMyConnectedStoreMutation();
  const { enqueueSnackbar } = useSnackbar();
  const isUniqueStore = !!profile?.me?.permissions?.length && profile?.me?.permissions?.length <= 1;

  const mediaQuery = 'sm';
  const imagePlaceholder =
    'https://image.crisp.chat/avatar/website/67d5e673-0291-45e6-8eb5-2b0e1aa79e06/120/?1720676554415';

  // widest store name defines the menu width, since the Scrollbar's SimpleBar
  // wrapper uses absolutely-positioned inner nodes that can't shrink-to-fit content
  const longestStoreNameLength = Math.max(
    0,
    ...(profile.me?.permissions.map((permission) => permission?.store?.name?.length ?? 0) ?? [])
  );
  const menuWidth = Math.min(Math.max(longestStoreNameLength * 9 + 100, 250), 330);

  const planChoices = {
    FREE: 'Free',
    PRO: 'Pro',
    IMPULSO: 'Impulso',
    BASICO: 'Básico',
    INTERMEDIARIO: 'Intermediário',
    AVANCADO: 'Avançado',
  };

  async function onUpdateConnectedStore(storeId: string) {
    try {
      popover.onClose();
      const { data } = await executeUpdateConnectedStore({ variables: { storeId } });
      if (data?.updateUserConnectedStore?.ok) {
        enqueueSnackbar('Loja alterada com sucesso!');
        window.location.reload();
        return;
      }
      enqueueSnackbar('Erro ao alterar loja!', { variant: 'error' });
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });
        return;
      }
      enqueueSnackbar('Erro ao alterar loja!', { variant: 'error' });
    }
  }

  if (isUniqueStore) {
    return (
      <>
        {profile?.me?.connectedStore?.profileImage ? (
          <Box
            component="img"
            alt="Logo da Loja"
            src={profile.me.connectedStore.profileImage}
            sx={{ width: 24, height: 24, borderRadius: '50%' }}
          />
        ) : (
          <Box
            component="img"
            alt="Logo da Loja"
            src="/avatar/zeki.png"
            sx={{ width: 24, height: 24, borderRadius: '50%' }}
          />
        )}

        <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
          {profile?.me?.connectedStore?.name}
        </Typography>

        {profile?.me?.connectedStore?.isActive && (
          <Label
            color={profile?.me?.connectedStore?.plan === 'FREE' ? 'default' : 'info'}
            sx={{
              height: 22,
              flexShrink: 0,
              display: { xs: 'none', [mediaQuery]: 'inline-flex' },
            }}
          >
            {planChoices[profile?.me?.connectedStore?.plan as keyof typeof planChoices]}
          </Label>
        )}

        {!profile?.me?.connectedStore?.isActive && (
          <Label color="default" variant="outlined" sx={{ flexShrink: 0 }}>
            Inativo
          </Label>
        )}
      </>
    );
  }

  return (
    <>
      <LoadingButton
        sx={{
          py: 0.5,
          gap: { xs: 0.5, md: 1 },
          backgroundColor: popover.open ? 'grey.300' : 'inherit',
          border: popover.open ? '2px solid grey' : '2px solid transparent',
          '&:hover': {
            backgroundColor: popover.open ? 'grey.300' : 'grey.100',
          },
          cursor: 'pointer',
        }}
        onClick={popover.onOpen}
        loading={updateConnectedStoreData.loading}
        disabled={updateConnectedStoreData.loading}
        aria-disabled="true"
      >
        {profile?.me?.connectedStore?.profileImage && (
          <Box
            component="img"
            alt="Logo da Loja"
            src={profile.me.connectedStore.profileImage}
            sx={{ width: 24, height: 24, borderRadius: '50%' }}
          />
        )}
        <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
          {profile?.me?.connectedStore?.name}
        </Typography>

        {profile?.me?.connectedStore?.isActive && (
          <Label
            color={profile?.me?.connectedStore?.plan === 'FREE' ? 'default' : 'info'}
            sx={{
              height: 22,
              flexShrink: 0,
              display: { xs: 'none', [mediaQuery]: 'inline-flex' },
            }}
          >
            {planChoices[profile?.me?.connectedStore?.plan as keyof typeof planChoices]}
          </Label>
        )}

        {!profile?.me?.connectedStore?.isActive && (
          <Label color="default" variant="outlined" sx={{ flexShrink: 0 }}>
            Inativo
          </Label>
        )}

        <Iconify
          width={16}
          icon="carbon:chevron-sort"
          color={popover.open ? 'inherit' : 'default'}
        />
      </LoadingButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="top-left" sx={{ mt: 1 }}>
        <MenuList sx={{ minWidth: 250, width: menuWidth, maxWidth: 330 }}>
          <Scrollbar sx={{ maxHeight: 550, paddingRight: 1 }} autoHide={false}>
            {profile.me?.permissions.map((permission) => (
              <MenuItem
                key={`user-permission-id-${permission.id}`}
                sx={{ height: 48, gap: 2 }}
                selected={permission?.store?.id === profile?.me?.connectedStore?.id}
                onClick={() => {
                  if (permission?.store?.id === profile?.me?.connectedStore?.id) {
                    popover.onClose();
                    return;
                  }
                  onUpdateConnectedStore(permission.store.id);
                }}
              >
                <Avatar
                  alt="Logo da Loja"
                  src={permission.store.profileImage || imagePlaceholder}
                  sx={{ width: 24, height: 24 }}
                />

                <ListItemText
                  primary={permission?.store?.name}
                  secondary={userTypeChoices[permission?.userType as PermissionUserType]}
                  primaryTypographyProps={{ typography: 'subtitle2', noWrap: true }}
                  secondaryTypographyProps={{ component: 'span', noWrap: true }}
                  sx={{ flexGrow: 1, minWidth: 0 }}
                />

                {permission?.store?.isActive && (
                  <Label
                    color={permission?.store?.plan === 'FREE' ? 'default' : 'info'}
                    sx={{ flexShrink: 0 }}
                  >
                    {planChoices[permission?.store?.plan as keyof typeof planChoices]}
                  </Label>
                )}

                {!permission?.store?.isActive && (
                  <Label color="default" variant="outlined" sx={{ flexShrink: 0 }}>
                    Inativo
                  </Label>
                )}
              </MenuItem>
            ))}
          </Scrollbar>
        </MenuList>
      </CustomPopover>
    </>
  );
}
